/**
 * One source of truth for "where does clicking this card go?"
 *
 * - tv_show items with a category route to the series page so the viewer
 *   can pick an episode rather than diving straight into the first one
 *   the playlist happens to list.
 * - Everything else goes to /watch/:id as before.
 *
 * Context overrides matter:
 *   - "continue", "history", "highly_recommended" rows refer to a specific
 *     episode the viewer was on, so they should still link to /watch.
 */
export interface LinkableItem {
  id: string;
  kind: string;
  category?: string | null;
}

export function linkForItem(item: LinkableItem, opts: { episodeLevel?: boolean } = {}): string {
  // Virtual Real-Debrid series ids come in two shapes: bare
  // ("rd:series:tt12345") names the show, episode-qualified
  // ("rd:series:tt12345:S01E04") names one episode. The series page only
  // understands the bare shape, so strip the suffix — except in resume
  // contexts (episodeLevel), where the click means "keep watching THAT
  // episode" and goes straight to the player.
  if (item.id.startsWith("rd:series:")) {
    const isEpisode = /:S\d{1,3}E\d{1,3}$/.test(item.id);
    if (isEpisode && opts.episodeLevel) {
      return `/watch/${encodeURIComponent(item.id)}`;
    }
    const bare = item.id.replace(/:S\d{1,3}E\d{1,3}$/, "");
    return `/series/${encodeURIComponent(bare)}`;
  }
  if (item.kind === "tv_show" && item.category && !opts.episodeLevel) {
    return `/series/${encodeURIComponent(item.category)}`;
  }
  return `/watch/${encodeURIComponent(item.id)}`;
}
